import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import { Button, colors, Typography } from '@sweep-io/sweep-design';
import OnboardingLayout from './OnboardingLayout';
import Slide from '@mui/material/Slide';
import { RefObject } from 'react';

interface CreateAccountPageContentProps {
  fullName?: string;
  setFullName: (value: string) => void;
  accountName: string;
  setAccountName: (value: string) => void;
  isSubmitting: boolean;
  isSubmitted: boolean;
  onClick: () => void;
  accountField: RefObject<HTMLDivElement>;
  title?: string;
  goBack?: () => void;
}

const inputProps = {
  inputProps: {
    min: 1,
  },
  sx: {
    padding: '14px 24px',
    height: '48px',
    borderRadius: '24px',
    input: {
      paddingLeft: 0,
      backgroundClip: 'text!important', //to remove Chrome's background color if using autocomplete
    },
  },
};

//"important" is needed to override the definition in "AppTheme", because the inputs in this page have a different style.
const textFieldSx = {
  '.MuiFormHelperText-root': {
    marginLeft: '24px!important',
  },
};

export const CreateAccountPageContent = ({
  fullName,
  setFullName,
  accountName,
  setAccountName,
  isSubmitting,
  isSubmitted,
  onClick,
  accountField,
  title,
  goBack,
}: CreateAccountPageContentProps) => {
  const isCompanyNameInvalid = isSubmitted && (!accountName || accountName.trim().length < 1);
  return (
    <OnboardingLayout withLogout={true} goBack={goBack}>
      <Slide direction="left" in={true} mountOnEnter unmountOnExit>
        <Stack gap={3} alignItems="center">
          <Typography variant="h1-medium" color={colors.grey[900]}>
            {title ? title : 'Just a few more details'}
          </Typography>
          <Stack gap={2} alignItems="center" width="320px">
            <FormControl sx={{ width: '100%' }}>
              <TextField
                autoFocus
                value={fullName ?? ''}
                onChange={(event) => {
                  setFullName(event.target.value);
                }}
                InputProps={inputProps}
                placeholder="Enter full name"
                disabled={isSubmitting}
                sx={textFieldSx}
                data-testid="onboarding-full-name-input"
              />
            </FormControl>
            <FormControl sx={{ width: '100%' }}>
              <TextField
                inputRef={accountField}
                value={accountName}
                onChange={(event) => {
                  setAccountName(event.target.value);
                }}
                InputProps={inputProps}
                disabled={isSubmitting}
                placeholder="Enter company name"
                helperText={isCompanyNameInvalid ? 'Company name is required' : ''}
                error={isCompanyNameInvalid}
                sx={textFieldSx}
                data-testid="onboarding-company-name-input"
              />
            </FormControl>
            <Box width="100%" sx={{ button: { width: '100%' } }}>
              <Button size="medium" onClick={onClick} loading={isSubmitting}>
                Continue
              </Button>
            </Box>
          </Stack>
          <Box width="60%" flex={1} textAlign="center">
            <Typography variant="caption" color={colors.grey[800]}>
              By clicking "Continue" you agree to Sweep's
            </Typography>
            <Link
              sx={{ display: 'inline', fontWeight: 400, fontSize: '12px', ml: '4px' }}
              target="_blank"
              rel="noreferrer"
              href="https://www.sweep.io/terms-of-service/"
            >
              Terms of Service
            </Link>
          </Box>
        </Stack>
      </Slide>
    </OnboardingLayout>
  );
};
